// TelcoTrack — Úřady: stavy případů, povinností (dokumentů) a komunikace

import {
  AUTHORITY_CONSTRUCTION_PREP_KEYS,
  AUTHORITY_CONSTRUCTION_PREP_LABELS_CS,
  AUTHORITY_CONSTRUCTION_PREP_LEGACY_LABELS_CS,
  type AuthorityConstructionPrepKey,
  type AuthorityConstructionPrepLegacyKey,
} from './authority-domain.constants';
import {
  discoveryEntry,
  isNetworkStakeoutMatrixKey,
  NETWORK_STAKEOUT_DOCUMENT_TYPE,
  NETWORK_STAKEOUT_LABEL_CS,
  networkSubjectFromStakeoutMatrixKey,
} from './authority-urady-categories.constants';
import {
  hasBremenaRole,
  hasDatovkaRole,
  hasPripravaStavebRole,
  hasSuperAdminRole,
} from './roles.constants';

/** Příprava staveb agenda (křížení / povinnosti). */
export function canAccessAuthorityPrepAgenda(roles: string[] | null | undefined): boolean {
  return hasPripravaStavebRole(roles);
}

/**
 * Inženýring agenda (role key `bremena`) — dotčené orgány, věcná břemena.
 */
export function canAccessAuthorityBremenaAgenda(roles: string[] | null | undefined): boolean {
  return hasBremenaRole(roles);
}

/** @see canAccessAuthorityBremenaAgenda — menu / hub label Inženýring. */
export function canAccessAuthorityInzenyringAgenda(roles: string[] | null | undefined): boolean {
  return canAccessAuthorityBremenaAgenda(roles);
}

/** Gmail schránka v hubu Komunikace — Příprava staveb nebo Inženýring. */
export function canViewAuthorityEmailMailbox(roles: string[] | null | undefined): boolean {
  return hasPripravaStavebRole(roles) || hasBremenaRole(roles);
}

/**
 * Datová schránka ISDS (Komunikace → Datovka) — pouze role `datovka`.
 * Bez auto-přístupu admin/superadmin.
 */
export function canViewAuthorityIsdsMailbox(roles: string[] | null | undefined): boolean {
  return hasDatovkaRole(roles);
}

/**
 * Schválení odchozí datovky (Komunikace → Ke schválení) a okamžité odeslání ze schránky —
 * pouze role `datovka`.
 */
export function canApproveAuthorityIsdsOutbound(roles: string[] | null | undefined): boolean {
  return hasDatovkaRole(roles);
}

/** @see canApproveAuthorityIsdsOutbound — odeslání z org. schránky ISDS. */
export function canExecuteAuthorityIsdsSend(roles: string[] | null | undefined): boolean {
  return canApproveAuthorityIsdsOutbound(roles);
}

/** Hub Komunikace — Email a/nebo Datovka / Ke schválení / Nastavení schránek. */
export function canAccessAuthorityKomunikaceAgenda(roles: string[] | null | undefined): boolean {
  return (
    canViewAuthorityEmailMailbox(roles) ||
    canViewAuthorityIsdsMailbox(roles) ||
    canApproveAuthorityIsdsOutbound(roles) ||
    canConnectAuthorityMailbox(roles)
  );
}

/** Entire `/urady` module: prep, inženýring, or komunikace (no auto-access for admin/superadmin). */
export function canAccessAuthoritiesModule(roles: string[] | null | undefined): boolean {
  return (
    canAccessAuthorityPrepAgenda(roles) ||
    canAccessAuthorityBremenaAgenda(roles) ||
    canAccessAuthorityKomunikaceAgenda(roles)
  );
}

/**
 * Připojení / odpojení sdílené Gmail schránky (Komunikace → Nastavení).
 * Superadmin nebo role s e-mailovou schránkou (Příprava / Inženýring).
 */
export function canConnectAuthorityEmailMailbox(roles: string[] | null | undefined): boolean {
  return hasSuperAdminRole(roles) || canViewAuthorityEmailMailbox(roles);
}

/**
 * Zapnutí / správa ISDS schránky (Komunikace → Nastavení).
 * Superadmin nebo role `datovka` (odesílání ze schránky zůstává jen `datovka`).
 */
export function canConnectAuthorityIsdsMailbox(roles: string[] | null | undefined): boolean {
  return hasSuperAdminRole(roles) || hasDatovkaRole(roles);
}

/** Vidí záložku Nastavení v Komunikaci (alespoň jeden kanál ke správě). */
export function canConnectAuthorityMailbox(roles: string[] | null | undefined): boolean {
  return canConnectAuthorityEmailMailbox(roles) || canConnectAuthorityIsdsMailbox(roles);
}

/**
 * Compose / request send via e-mail — Příprava staveb i Inženýring (workflow Napsat).
 * Schránku Gmail vidí {@link canViewAuthorityEmailMailbox}.
 */
export function canSendViaAuthorityEmailChannel(roles: string[] | null | undefined): boolean {
  return hasPripravaStavebRole(roles) || hasBremenaRole(roles);
}

/**
 * Compose / request send via ISDS — Příprava, Inženýring i Datovka.
 * Okamžité odeslání ze schránky jen {@link canExecuteAuthorityIsdsSend};
 * ostatní role vytváří položku Ke schválení.
 */
export function canSendViaAuthorityIsdsChannel(roles: string[] | null | undefined): boolean {
  return hasPripravaStavebRole(roles) || hasBremenaRole(roles) || hasDatovkaRole(roles);
}

/** Portal (křížení sítí) follows Příprava staveb agenda. */
export function canSendViaAuthorityPortalChannel(roles: string[] | null | undefined): boolean {
  return canAccessAuthorityPrepAgenda(roles);
}

/** Paper postal pack — Inženýring (břemena K podání; ve schránce Datovka). */
export function canSendViaAuthorityPaperChannel(roles: string[] | null | undefined): boolean {
  return canAccessAuthorityBremenaAgenda(roles) || hasDatovkaRole(roles);
}

/** Any outbound send channel the user may use. */
export function canSendViaAuthorityMailbox(roles: string[] | null | undefined): boolean {
  return (
    canSendViaAuthorityEmailChannel(roles) ||
    canSendViaAuthorityIsdsChannel(roles) ||
    canSendViaAuthorityPortalChannel(roles)
  );
}

export function canSendViaAuthorityChannel(
  roles: string[] | null | undefined,
  channel: string | null | undefined,
): boolean {
  const key = (channel ?? '').trim().toLowerCase();
  if (key === 'email') return canSendViaAuthorityEmailChannel(roles);
  if (key === 'isds') return canSendViaAuthorityIsdsChannel(roles);
  if (key === 'portal') return canSendViaAuthorityPortalChannel(roles);
  if (key === 'paper') return canSendViaAuthorityPaperChannel(roles);
  return false;
}

/** Sidebar / hub shell mode — three menus sharing `/urady` routes. */
export type AuthorityHubMode = 'inzenyring' | 'priprava' | 'komunikace';

/** Hub tabs under Inženýring / Příprava staveb / Komunikace. */
export type AuthorityHubTabId =
  | 'prehled'
  | 'posta'
  | 'datovka'
  | 'k-schvaleni'
  | 'bremena'
  | 'dotcene-organy'
  | 'krizeni-siti'
  | 'vytyceni'
  | 'povinnosti'
  | 'kontakty'
  | 'nastaveni';

/** Tabs belonging to each hub menu (before role filter). */
export const AUTHORITY_HUB_MODE_TABS: Record<AuthorityHubMode, readonly AuthorityHubTabId[]> = {
  inzenyring: ['prehled', 'bremena', 'dotcene-organy', 'kontakty'],
  priprava: ['prehled', 'krizeni-siti', 'vytyceni', 'povinnosti', 'kontakty'],
  komunikace: ['posta', 'datovka', 'k-schvaleni'],
};

/**
 * Nav matrix:
 * - Příprava staveb (`priprava_staveb`): Přehled, Křížení sítí, Vytyčení, Povinnosti, Kontakty
 * - Inženýring (`bremena`): Přehled, Břemena, Dotčené orgány, Kontakty
 * - Komunikace: Email, Datovka (`datovka`), Ke schválení (`datovka`) — připojení schránek v App Nastavení
 * - Přehled role access stays module-wide so shared stavba deep links work
 * - Nastavení jen v Komunikaci — Gmail: prep/inženýring/superadmin; ISDS: datovka/superadmin
 */
export function canAccessAuthorityHubTab(
  roles: string[] | null | undefined,
  tab: AuthorityHubTabId,
): boolean {
  switch (tab) {
    case 'prehled':
    case 'kontakty':
      return (
        canAccessAuthorityPrepAgenda(roles) || canAccessAuthorityBremenaAgenda(roles)
      );
    case 'posta':
      return canViewAuthorityEmailMailbox(roles);
    case 'krizeni-siti':
    case 'vytyceni':
    case 'povinnosti':
      return canAccessAuthorityPrepAgenda(roles);
    case 'datovka':
      return canViewAuthorityIsdsMailbox(roles);
    case 'k-schvaleni':
      return canApproveAuthorityIsdsOutbound(roles);
    case 'bremena':
    case 'dotcene-organy':
      return canAccessAuthorityBremenaAgenda(roles);
    case 'nastaveni':
      return canConnectAuthorityMailbox(roles);
    default:
      return false;
  }
}

/** Hub tab visible in this menu mode and allowed for the user's roles. */
export function canAccessAuthorityHubTabInMode(
  roles: string[] | null | undefined,
  tab: AuthorityHubTabId,
  mode: AuthorityHubMode,
): boolean {
  if (!(AUTHORITY_HUB_MODE_TABS[mode] as readonly string[]).includes(tab)) return false;
  return canAccessAuthorityHubTab(roles, tab);
}

export const AUTHORITY_CASE_TYPES = ['doss', 'povoleni', 'majetkopravni', 'ostatni'] as const;
export type AuthorityCaseType = (typeof AUTHORITY_CASE_TYPES)[number];

export const AUTHORITY_CASE_STATUSES = [
  'new',
  'triaged',
  'in_progress',
  'waiting_external',
  'waiting_internal',
  'approval',
  'done',
  'cancelled',
] as const;
export type AuthorityCaseStatus = (typeof AUTHORITY_CASE_STATUSES)[number];

/**
 * Thread lifecycle for orgán / křížení / povinnost (authority_obligations.status).
 * User-facing „vlákno“ — not per-message. `n_a` stays for prep „nepoužije se“.
 */
export const AUTHORITY_THREAD_STATUSES = [
  'not_requested',
  'waiting_statement',
  'overdue',
  'approved',
  'needs_supplement',
  'needs_classification',
] as const;
export type AuthorityThreadStatus = (typeof AUTHORITY_THREAD_STATUSES)[number];

export const AUTHORITY_OBLIGATION_STATUSES = [
  'not_requested',
  'waiting_statement',
  'overdue',
  'approved',
  'needs_supplement',
  'needs_classification',
  'n_a',
] as const;
export type AuthorityObligationStatus = (typeof AUTHORITY_OBLIGATION_STATUSES)[number];

export const AUTHORITY_THREAD_STATUS_LABELS_CS: Record<AuthorityThreadStatus, string> = {
  not_requested: 'Nezažádáno',
  waiting_statement: 'Zažádáno',
  overdue: 'Po lhůtě',
  approved: 'Schváleno',
  needs_supplement: 'Nutné doplnění',
  needs_classification: 'K zařazení',
};

export const AUTHORITY_THREAD_STATUS_TONES: Record<
  AuthorityThreadStatus,
  'success' | 'warning' | 'error' | 'info' | 'neutral'
> = {
  not_requested: 'error',
  waiting_statement: 'warning',
  overdue: 'error',
  approved: 'success',
  needs_supplement: 'info',
  needs_classification: 'warning',
};

/**
 * Legacy obligation / thread codes (pre thread-status rename). Map for display + transitions.
 */
export const AUTHORITY_OBLIGATION_LEGACY_STATUS_ALIASES: Readonly<Record<string, AuthorityObligationStatus>> = {
  waiting: 'waiting_statement',
  missing: 'not_requested',
  requested: 'waiting_statement',
  supplement: 'needs_supplement',
  received: 'approved',
  expired: 'overdue',
};

export function isAuthorityThreadStatus(value: string | null | undefined): value is AuthorityThreadStatus {
  return (AUTHORITY_THREAD_STATUSES as readonly string[]).includes((value ?? '').trim());
}

export function normalizeAuthorityObligationStatus(
  value: string | null | undefined,
): AuthorityObligationStatus | null {
  const key = (value ?? '').trim();
  if (!key) return null;
  if (isAuthorityObligationStatus(key)) return key;
  return AUTHORITY_OBLIGATION_LEGACY_STATUS_ALIASES[key] ?? null;
}

export function normalizeAuthorityThreadStatus(
  value: string | null | undefined,
): AuthorityThreadStatus | null {
  const normalized = normalizeAuthorityObligationStatus(value);
  if (!normalized || normalized === 'n_a') return null;
  return normalized;
}

export const AUTHORITY_CASE_TERMINAL_STATUSES: readonly AuthorityCaseStatus[] = ['done', 'cancelled'];

/**
 * Allowed case lifecycle transitions. This is the single source of truth for
 * both human edits and automated mail processing.
 */
export const AUTHORITY_CASE_STATUS_TRANSITIONS: Readonly<
  Record<AuthorityCaseStatus, readonly AuthorityCaseStatus[]>
> = {
  new: ['triaged', 'cancelled'],
  triaged: ['in_progress', 'waiting_external', 'waiting_internal', 'approval', 'cancelled'],
  in_progress: ['waiting_external', 'waiting_internal', 'approval', 'done', 'cancelled'],
  waiting_external: ['in_progress', 'waiting_internal', 'approval', 'done', 'cancelled'],
  waiting_internal: ['in_progress', 'waiting_external', 'approval', 'done', 'cancelled'],
  approval: ['in_progress', 'waiting_external', 'waiting_internal', 'done', 'cancelled'],
  done: ['in_progress'],
  cancelled: ['new'],
};

export function canTransitionAuthorityCaseStatus(
  from: string | null | undefined,
  to: string | null | undefined,
): boolean {
  if (!from || !to || !isAuthorityCaseStatus(from) || !isAuthorityCaseStatus(to)) return false;
  if (from === to) return true;
  return AUTHORITY_CASE_STATUS_TRANSITIONS[from].includes(to);
}

export const AUTHORITY_OBLIGATION_CLOSED_STATUSES: readonly AuthorityObligationStatus[] = [
  'approved',
  'n_a',
];

export const AUTHORITY_OBLIGATION_OPEN_STATUSES: readonly AuthorityObligationStatus[] = [
  'not_requested',
  'waiting_statement',
  'overdue',
  'needs_supplement',
  'needs_classification',
];

/**
 * Thread lifecycle transitions (SSOT for human Hotovo, outbound send, AI, organ deadline).
 *
 * not_requested → waiting_statement   systém po odchozím odeslání (e-mail/ISDS)
 *                                     nebo zařazení ISDS do Ke schválení (Datovka)
 * waiting_statement → overdue         systém (dotčené orgány, 30 dní po odeslání)
 * waiting_statement|overdue → approved AI high-confidence kladné / uživatel Hotovo
 * * → needs_supplement                AI výzva k doplnění dokumentů
 * * → needs_classification            AI nejistota
 * approved → not_requested|waiting_statement  uživatel vrátí Hotovo
 * * → n_a                             prep výjimka; n_a → not_requested = reaktivace
 */
export const AUTHORITY_OBLIGATION_STATUS_TRANSITIONS: Readonly<
  Record<AuthorityObligationStatus, readonly AuthorityObligationStatus[]>
> = {
  not_requested: ['waiting_statement', 'approved', 'needs_supplement', 'needs_classification', 'n_a'],
  waiting_statement: [
    'overdue',
    'needs_supplement',
    'approved',
    'needs_classification',
    'not_requested',
    'n_a',
  ],
  overdue: ['needs_supplement', 'approved', 'waiting_statement', 'needs_classification', 'not_requested', 'n_a'],
  needs_supplement: ['waiting_statement', 'approved', 'needs_classification', 'not_requested', 'n_a'],
  approved: ['not_requested', 'waiting_statement', 'needs_supplement', 'overdue', 'needs_classification', 'n_a'],
  needs_classification: [
    'not_requested',
    'waiting_statement',
    'approved',
    'needs_supplement',
    'overdue',
    'n_a',
  ],
  n_a: ['not_requested'],
};

export function canTransitionAuthorityObligationStatus(
  from: string | null | undefined,
  to: string | null | undefined,
): boolean {
  const fromStatus = normalizeAuthorityObligationStatus(from);
  const toStatus = normalizeAuthorityObligationStatus(to);
  if (!fromStatus || !toStatus) return false;
  if (fromStatus === toStatus) return true;
  return AUTHORITY_OBLIGATION_STATUS_TRANSITIONS[fromStatus].includes(toStatus);
}

/** Who last set the obligation/case status. */
export const AUTHORITY_STATUS_SOURCES = ['ai', 'rule', 'human', 'system'] as const;
export type AuthorityStatusSource = (typeof AUTHORITY_STATUS_SOURCES)[number];

/** AI classification of an inbound authority response at obligation level. */
export const AUTHORITY_RESPONSE_OUTCOMES = [
  'consent',
  'consent_with_conditions',
  'refusal',
  'supplement',
  'other',
] as const;
export type AuthorityResponseOutcome = (typeof AUTHORITY_RESPONSE_OUTCOMES)[number];

export function isAuthorityResponseOutcome(value: string): value is AuthorityResponseOutcome {
  return (AUTHORITY_RESPONSE_OUTCOMES as readonly string[]).includes(value);
}

/** Auto-apply obligation outcome (received/supplement) at or above this confidence. */
export const AUTHORITY_MAIL_AUTO_OUTCOME_THRESHOLD_DEFAULT = 0.92;
/** Below auto threshold but at/above this → UI suggestion (Přijmout / Odmítnout). */
export const AUTHORITY_MAIL_SUGGEST_OUTCOME_THRESHOLD_DEFAULT = 0.7;

/** Target thread status for an AI response outcome (refusal/other never map to a status). */
export function authorityObligationStatusForOutcome(
  outcome: string | null | undefined,
): AuthorityObligationStatus | null {
  if (outcome === 'consent' || outcome === 'consent_with_conditions') return 'approved';
  if (outcome === 'supplement') return 'needs_supplement';
  return null;
}

/** Mail-intake review buckets (SSOT; dříve jen DB CHECK + FE stringy). */
export const AUTHORITY_MESSAGE_REVIEW_STATUSES = [
  'processing',
  'applied',
  'needs_review',
  'unmatched',
  'irrelevant',
] as const;
export type AuthorityMessageReviewStatus = (typeof AUTHORITY_MESSAGE_REVIEW_STATUSES)[number];

export const AUTHORITY_MESSAGE_OUTBOUND_STATUSES = ['draft', 'queued', 'sent', 'failed'] as const;
export type AuthorityMessageOutboundStatus = (typeof AUTHORITY_MESSAGE_OUTBOUND_STATUSES)[number];

/** Collapsed checklist vocabulary shared by matrix, detail and edit UI. */
export type AuthorityObligationStatusKind = 'done' | 'waiting' | 'action' | 'missing' | 'na';

export function authorityObligationStatusKind(
  status: string | null | undefined,
): AuthorityObligationStatusKind {
  const normalized = normalizeAuthorityObligationStatus(status);
  switch (normalized) {
    case 'approved':
      return 'done';
    case 'waiting_statement':
    case 'overdue':
      return 'waiting';
    case 'needs_supplement':
    case 'needs_classification':
      return 'action';
    case 'n_a':
      return 'na';
    default:
      return 'missing';
  }
}

/** Checklist „splněná“ = Schváleno. */
export function isAuthorityObligationDoneStatus(status: string | null | undefined): boolean {
  return authorityObligationStatusKind(status) === 'done';
}

/**
 * Next status for reversible checklist toggle (splněná ↔ nesplněná).
 * Done → prefers `not_requested`, else `waiting_statement`. Not-done → `approved`. `n_a` stays locked.
 */
export function toggleAuthorityObligationDoneStatus(
  from: string | null | undefined,
): AuthorityObligationStatus | null {
  const status = normalizeAuthorityObligationStatus(from);
  if (!status) return null;
  const kind = authorityObligationStatusKind(status);
  if (kind === 'na') return null;
  if (kind === 'done') {
    if (canTransitionAuthorityObligationStatus(status, 'not_requested')) return 'not_requested';
    if (canTransitionAuthorityObligationStatus(status, 'waiting_statement')) return 'waiting_statement';
    return null;
  }
  if (canTransitionAuthorityObligationStatus(status, 'approved')) return 'approved';
  return null;
}


/**
 * Document types from seeded obligation templates (stable column order).
 * Do not invent extra keys here — live/custom types append after this catalog.
 */
export const AUTHORITY_DOCUMENT_TYPES = [
  'vyjadreni_doss',
  'stanovisko_hzs',
  'souhlas_vlastnika',
  'vecne_bremeno',
  'vypis_kn',
] as const;
export type AuthorityDocumentType = (typeof AUTHORITY_DOCUMENT_TYPES)[number];

export const AUTHORITY_DOCUMENT_TYPE_LABELS_CS: Record<AuthorityDocumentType, string> = {
  vyjadreni_doss: 'Vyjádření DOSS',
  stanovisko_hzs: 'Stanovisko HZS',
  souhlas_vlastnika: 'Souhlas vlastníka',
  vecne_bremeno: 'Věcné břemeno',
  vypis_kn: 'Výpis KN',
};

/** Column / Pošta TYP chips. Full Czech stays on `authorityDocumentTypeLabel`. */
export const AUTHORITY_DOCUMENT_TYPE_SHORT_LABELS_CS: Record<AuthorityDocumentType, string> = {
  vyjadreni_doss: 'DOSS',
  stanovisko_hzs: 'HZS',
  souhlas_vlastnika: 'Souhlas',
  vecne_bremeno: 'Břemeno',
  vypis_kn: 'KN',
};

/**
 * Compact display for subject keys, prep keys and live mail types.
 * API keys stay snake_case; only the visible chip text is abbreviated.
 */
export const AUTHORITY_TYPE_CHIP_LABELS_CS: Record<string, string> = {
  ...AUTHORITY_DOCUMENT_TYPE_SHORT_LABELS_CS,
  vyjadreni_site: 'Sítě',
  krizeni_siti: 'Sítě',
  [NETWORK_STAKEOUT_DOCUMENT_TYPE]: 'Vytyč.',
  dio: 'DIO',
  aggregates: 'Mater.',
  container_hauler: 'Dopravce',
  mobile_wc: 'WC',
  handover_to_site_lead: 'Předání',
  recycling_site: 'Skládka',
  material_storage: 'Deponie',
  stakeout: 'Vytyč.',
  renew_statements: 'Obnova',
  traffic_signs: 'DZ',
  archeologicky_ustav: 'Archeo',
  obec: 'Obec',
  lesy_cr: 'Lesy ČR',
  mo_sem: 'MO',
  sus: 'SÚS',
  stavebni_urad: 'SÚ',
  jiny_verejny_vlastnik: 'J. vl.',
  silnicni_spravni_urad: 'SSÚ',
  md: 'MD',
  kraj_ssu: 'Kraj I.',
  obecni_urad_komunikace: 'OÚ MK',
  dopravni_urad: 'Dopr. úř.',
  drazni_urad: 'Drážní',
  povodi: 'Povodí',
  vodopravni: 'Vodopr.',
  koordinovane_stanovisko: 'Koord.',
  organ_lesu: 'SSL',
  organ_zpf: 'ZPF',
  pamatkova_pece: 'PP',
  bansky_urad: 'OBU',
  cgs_chlu: 'CHLÚ',
  ucl: 'ÚCL',
  plavebni_sprava: 'Plav.',
  custom_organ: 'Vlastní',
  elektronicke_komunikace: 'EK',
  elektrina: 'El.',
  vodovod: 'Vod.',
  kanalizace: 'Kan.',
  verejne_osvetleni: 'VO',
  telematika: 'Telem.',
  produktovody: 'Prod.',
  zavlahy: 'Závl.',
  obecni_site: 'Obec. sítě',
  rsd: 'ŘSD',
  spravce_komunikace: 'Spr. kom.',
  zeleznicni_infrastruktura: 'SŽ',
  zeleznicni_provozovatel: 'Provoz. dr.',
  zeleznicni_telekom_energie: 'Dr. tel.',
  custom_sit: 'Síť',
  spu: 'SPÚ',
  uzsvm: 'ÚZSVM',
  pcr: 'PČR',
  jes: 'JES',
  aopk: 'AOPK',
  npu: 'NPÚ',
  hzs: 'HZS',
  khs: 'KHS',
  plyn: 'Plyn',
  teplo: 'Teplo',
};

const LIVE_DOCUMENT_TYPE_SHORT_LABELS_CS: Record<string, string> = {
  stanovisko: 'Stan.',
  vyjadreni: 'Vyj.',
  vyjádření: 'Vyj.',
};

/**
 * Default checklist every stavba tracks in Případy / Dokumenty:
 * official document types + construction-prep keys. Live/custom extras append after.
 */
export const AUTHORITY_REQUIRED_DOCUMENT_TYPES = [
  ...AUTHORITY_DOCUMENT_TYPES,
  ...AUTHORITY_CONSTRUCTION_PREP_KEYS,
] as const;

export function isAuthorityDocumentType(value: string): value is AuthorityDocumentType {
  return (AUTHORITY_DOCUMENT_TYPES as readonly string[]).includes(value);
}

export function isAuthorityRequiredDocumentType(_value: string | null | undefined): boolean {
  return false;
}

/**
 * Keyword needles for inferring document type from inbound mail subject/body
 * when AI is unavailable. Match against folded (diacritic-stripped) text.
 * Longer / more specific phrases win over short tokens when multiple hit.
 */
export const AUTHORITY_MAIL_DOCUMENT_TYPE_KEYWORDS: ReadonlyArray<{
  documentType: string;
  keywords: readonly string[];
}> = [
  {
    documentType: 'dio',
    keywords: ['dopravne inzenyrske opatreni', 'dopravne inzenyrske', 'schvalene dio', 'dio'],
  },
  {
    documentType: 'vecne_bremeno',
    keywords: ['vecne bremeno', 'e vbr', 'evbr'],
  },
  {
    documentType: 'souhlas_vlastnika',
    keywords: ['souhlas vlastnika', 'souhlas s umistenim'],
  },
  {
    documentType: 'vypis_kn',
    keywords: ['vypis z kn', 'vypis kn', 'list vlastnictvi'],
  },
  {
    documentType: 'stanovisko_hzs',
    keywords: ['stanovisko hzs', 'hasicsky zachranny'],
  },
  {
    documentType: 'vyjadreni_doss',
    keywords: ['vyjadreni doss', 'vyjadreni k siti', 'stanovisko doss'],
  },
  {
    documentType: NETWORK_STAKEOUT_DOCUMENT_TYPE,
    keywords: ['vytyceni site', 'vytyceni infrastruktury', 'zadost o vytyceni'],
  },
  {
    documentType: 'stakeout',
    keywords: ['vytyceni'],
  },
  {
    documentType: 'mobile_wc',
    keywords: ['mobilni wc', 'toaleta dek'],
  },
  {
    documentType: 'handover_to_site_lead',
    keywords: ['predani staveniste', 'predani stavby'],
  },
];

function foldMailKeywordText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Infer a catalog document type from mail text via keyword heuristics.
 * Returns null when no unique match among allowed types.
 */
export function inferAuthorityMailDocumentType(
  searchable: string,
  allowedTypes?: readonly string[],
): { documentType: string; confidence: number; evidence: string } | null {
  const haystack = ` ${foldMailKeywordText(searchable)} `;
  const allowed =
    allowedTypes && allowedTypes.length
      ? new Set(allowedTypes.map((item) => item.trim()).filter(Boolean))
      : null;
  const hits: Array<{ documentType: string; keyword: string; length: number }> = [];
  for (const row of AUTHORITY_MAIL_DOCUMENT_TYPE_KEYWORDS) {
    if (allowed && !allowed.has(row.documentType)) continue;
    for (const raw of row.keywords) {
      const keyword = foldMailKeywordText(raw);
      if (keyword.length < 3) continue;
      if (haystack.includes(` ${keyword} `)) {
        hits.push({ documentType: row.documentType, keyword, length: keyword.length });
      }
    }
  }
  if (!hits.length) return null;
  hits.sort((a, b) => b.length - a.length || a.documentType.localeCompare(b.documentType));
  const best = hits[0];
  const competing = hits.find(
    (hit) => hit.documentType !== best.documentType && hit.length === best.length,
  );
  if (competing) return null;
  const confidence = best.length >= 12 ? 0.95 : best.length >= 6 ? 0.92 : 0.9;
  return {
    documentType: best.documentType,
    confidence,
    evidence: `document type keyword: ${best.keyword}`,
  };
}

/** Short Czech label; unknown live keys are humanized, not replaced with a fake catalog name. */
export function authorityDocumentTypeLabel(type: string | null | undefined): string {
  const key = (type ?? '').trim();
  if (!key) return '—';
  if (isAuthorityDocumentType(key)) return AUTHORITY_DOCUMENT_TYPE_LABELS_CS[key];
  if (key in AUTHORITY_CONSTRUCTION_PREP_LABELS_CS) {
    return AUTHORITY_CONSTRUCTION_PREP_LABELS_CS[key as AuthorityConstructionPrepKey];
  }
  if (key in AUTHORITY_CONSTRUCTION_PREP_LEGACY_LABELS_CS) {
    return AUTHORITY_CONSTRUCTION_PREP_LEGACY_LABELS_CS[key as AuthorityConstructionPrepLegacyKey];
  }
  const discovery = discoveryEntry(key);
  if (discovery?.labelCs) return discovery.labelCs;
  if (key === NETWORK_STAKEOUT_DOCUMENT_TYPE) return NETWORK_STAKEOUT_LABEL_CS;
  if (isNetworkStakeoutMatrixKey(key)) {
    const base = networkSubjectFromStakeoutMatrixKey(key);
    const networkLabel = base ? authorityDocumentTypeLabel(base) : NETWORK_STAKEOUT_LABEL_CS;
    return base ? `Vytyčení — ${networkLabel}` : NETWORK_STAKEOUT_LABEL_CS;
  }
  if (key === 'vyjadreni_site') return 'Vyjádření k síti';
  if (key === 'krizeni_siti') return 'Křížení sítí';
  if (key === 'stavebni_urad') return 'Stavební úřad';
  return key.replace(/_/g, ' ');
}

/** Compact chip / column label (Pošta TYP, Případy headers). Keys unchanged. */
export function authorityDocumentTypeShortLabel(type: string | null | undefined): string {
  const key = (type ?? '').trim();
  if (!key) return '—';
  if (isNetworkStakeoutMatrixKey(key)) {
    const base = networkSubjectFromStakeoutMatrixKey(key);
    return base ? authorityDocumentTypeShortLabel(base) : NETWORK_STAKEOUT_LABEL_CS;
  }
  if (key in AUTHORITY_TYPE_CHIP_LABELS_CS) return AUTHORITY_TYPE_CHIP_LABELS_CS[key];
  const live = LIVE_DOCUMENT_TYPE_SHORT_LABELS_CS[key.replace(/_/g, ' ').trim().toLocaleLowerCase('cs')];
  if (live) return live;
  return authorityDocumentTypeLabel(key);
}

/** Required catalog order first, then remaining live/optional types by Czech label. */
export function sortAuthorityDocumentTypes(types: readonly string[]): string[] {
  const catalogIndex = new Map<string, number>(
    AUTHORITY_REQUIRED_DOCUMENT_TYPES.map((id, index) => [id, index]),
  );
  const unique: string[] = [];
  const seen = new Set<string>();
  for (const raw of types) {
    const key = (raw ?? '').trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    unique.push(key);
  }
  return unique.sort((a, b) => {
    const ia = catalogIndex.get(a);
    const ib = catalogIndex.get(b);
    if (ia != null && ib != null) return ia - ib;
    if (ia != null) return -1;
    if (ib != null) return 1;
    return authorityDocumentTypeLabel(a).localeCompare(authorityDocumentTypeLabel(b), 'cs');
  });
}

/** Open-case priority for polygon stage (lower = more blocking). */
export const AUTHORITY_CASE_STAGE_RANK: Record<AuthorityCaseStatus, number> = {
  waiting_external: 1,
  waiting_internal: 2,
  approval: 3,
  in_progress: 4,
  triaged: 5,
  new: 6,
  done: 20,
  cancelled: 21,
};

export const AUTHORITY_MESSAGE_CHANNELS = ['email', 'isds', 'manual', 'portal', 'paper'] as const;
export type AuthorityMessageChannel = (typeof AUTHORITY_MESSAGE_CHANNELS)[number];

/**
 * Message channels visible in Email / Datovka mailboxes for the given roles.
 * Inbox visibility (not compose): e-mail ↔ prep/inženýring; ISDS + paper ↔ role `datovka`;
 * portal ↔ Příprava; manual when any mailbox channel is allowed.
 */
export function allowedAuthorityMessageChannels(
  roles: string[] | null | undefined,
): readonly AuthorityMessageChannel[] {
  const out: AuthorityMessageChannel[] = [];
  if (canViewAuthorityEmailMailbox(roles)) out.push('email');
  if (canSendViaAuthorityPortalChannel(roles)) out.push('portal');
  if (canViewAuthorityIsdsMailbox(roles)) {
    out.push('isds');
    out.push('paper');
  }
  if (out.length) out.push('manual');
  return out;
}

export const AUTHORITY_MESSAGE_DIRECTIONS = ['inbound', 'outbound'] as const;
export type AuthorityMessageDirection = (typeof AUTHORITY_MESSAGE_DIRECTIONS)[number];

export const AUTHORITY_MESSAGE_MATCH_STATUSES = [
  'auto_matched',
  'suggested',
  'unmatched',
  'confirmed',
] as const;
export type AuthorityMessageMatchStatus = (typeof AUTHORITY_MESSAGE_MATCH_STATUSES)[number];

/**
 * AI message classification (authority_messages.work_status). Read-only for users.
 * Distinct from pairing `match_status` and intake `review_status`.
 * Thread waiting/done semantics live on obligations (vlákno), not on messages.
 */
export const AUTHORITY_MESSAGE_CLASSIFICATION_STATUSES = [
  'unclassified',
  'classified',
  'irrelevant',
  'needs_filing',
] as const;
export type AuthorityMessageClassificationStatus =
  (typeof AUTHORITY_MESSAGE_CLASSIFICATION_STATUSES)[number];

/** @deprecated Use AUTHORITY_MESSAGE_CLASSIFICATION_STATUSES */
export const AUTHORITY_MESSAGE_WORK_STATUSES = AUTHORITY_MESSAGE_CLASSIFICATION_STATUSES;
/** @deprecated Use AuthorityMessageClassificationStatus */
export type AuthorityMessageWorkStatus = AuthorityMessageClassificationStatus;

export const AUTHORITY_MESSAGE_WORK_STATUS_DEFAULT = 'unclassified' as const;

export const AUTHORITY_MESSAGE_CLASSIFICATION_STATUS_LABELS_CS: Record<
  AuthorityMessageClassificationStatus,
  string
> = {
  unclassified: 'Nezařazená',
  classified: 'Zařazená',
  irrelevant: 'Nerelevantní',
  needs_filing: 'K zařazení',
};

/** @deprecated Use AUTHORITY_MESSAGE_CLASSIFICATION_STATUS_LABELS_CS */
export const AUTHORITY_MESSAGE_WORK_STATUS_LABELS_CS = AUTHORITY_MESSAGE_CLASSIFICATION_STATUS_LABELS_CS;

export const AUTHORITY_MESSAGE_CLASSIFICATION_STATUS_TONES: Record<
  AuthorityMessageClassificationStatus,
  'success' | 'warning' | 'error' | 'info' | 'neutral'
> = {
  unclassified: 'neutral',
  classified: 'success',
  irrelevant: 'error',
  needs_filing: 'warning',
};

/** @deprecated Use AUTHORITY_MESSAGE_CLASSIFICATION_STATUS_TONES */
export const AUTHORITY_MESSAGE_WORK_STATUS_TONES = AUTHORITY_MESSAGE_CLASSIFICATION_STATUS_TONES;

/** Legacy manual Pošta stavy → AI classification (waiting_* belonged on the thread). */
export const AUTHORITY_MESSAGE_WORK_STATUS_LEGACY_ALIASES: Readonly<
  Record<string, AuthorityMessageClassificationStatus>
> = {
  filed: 'classified',
  waiting_statement: 'needs_filing',
  waiting_documents: 'needs_filing',
  waiting_our_reply: 'needs_filing',
  waiting_their_reply: 'needs_filing',
  done: 'classified',
  spam: 'irrelevant',
};

export function isAuthorityMessageWorkStatus(
  value: string | null | undefined,
): value is AuthorityMessageWorkStatus {
  return (AUTHORITY_MESSAGE_CLASSIFICATION_STATUSES as readonly string[]).includes((value ?? '').trim());
}

export function normalizeAuthorityMessageWorkStatus(
  value: string | null | undefined,
): AuthorityMessageWorkStatus {
  const key = (value ?? '').trim();
  if (isAuthorityMessageWorkStatus(key)) return key;
  return AUTHORITY_MESSAGE_WORK_STATUS_LEGACY_ALIASES[key] ?? AUTHORITY_MESSAGE_WORK_STATUS_DEFAULT;
}

/** Closed for inbox KPIs: irrelevant only (spam is a separate mailbox flag). */
export function isAuthorityMessageWorkStatusClosed(value: string | null | undefined): boolean {
  return normalizeAuthorityMessageWorkStatus(value) === 'irrelevant';
}

/** @deprecated Spam is `spam_at`, not a classification status. */
export function isAuthorityMessageWorkStatusSpam(_value: string | null | undefined): boolean {
  return false;
}

/**
 * Derive classification after human/AI assignment to stavba + document type.
 * Does not invent thread waiting states on the message.
 */
export function deriveAuthorityMessageClassification(input: {
  polygonId?: string | null;
  documentType?: string | null;
  current?: string | null;
  forceIrrelevant?: boolean;
}): AuthorityMessageClassificationStatus {
  if (input.forceIrrelevant) return 'irrelevant';
  const current = normalizeAuthorityMessageWorkStatus(input.current);
  if (current === 'irrelevant') return 'irrelevant';
  const hasStavba = !!(input.polygonId ?? '').trim();
  const hasType = !!(input.documentType ?? '').trim();
  if (hasStavba && hasType) return 'classified';
  if (hasStavba || hasType) return 'needs_filing';
  return current === 'needs_filing' ? 'needs_filing' : 'unclassified';
}

/**
 * Resolve user-facing message „Stav“ from work_status + intake review_status.
 * Prefer review outcome when it encodes AI classification more accurately than a
 * stale work_status (e.g. legacy filed→classified remap left work_status wrong).
 */
export function resolveAuthorityMessageClassification(input: {
  workStatus?: string | null;
  reviewStatus?: string | null;
  polygonId?: string | null;
  documentType?: string | null;
}): AuthorityMessageClassificationStatus {
  const review = (input.reviewStatus ?? '').trim();
  if (review === 'irrelevant') return 'irrelevant';
  if (review === 'needs_review' || review === 'unmatched') return 'needs_filing';
  if (review === 'processing') return 'unclassified';
  if (review === 'applied') {
    return deriveAuthorityMessageClassification({
      polygonId: input.polygonId,
      documentType: input.documentType,
      current: 'classified',
    });
  }
  return normalizeAuthorityMessageWorkStatus(input.workStatus);
}

export function isAuthorityCaseType(value: string): value is AuthorityCaseType {
  return (AUTHORITY_CASE_TYPES as readonly string[]).includes(value);
}

export function isAuthorityCaseStatus(value: string): value is AuthorityCaseStatus {
  return (AUTHORITY_CASE_STATUSES as readonly string[]).includes(value);
}

export function isAuthorityObligationStatus(value: string): value is AuthorityObligationStatus {
  return (AUTHORITY_OBLIGATION_STATUSES as readonly string[]).includes(value);
}

export function isAuthorityMessageChannel(value: string): value is AuthorityMessageChannel {
  return (AUTHORITY_MESSAGE_CHANNELS as readonly string[]).includes(value);
}

export function isAuthorityMessageDirection(value: string): value is AuthorityMessageDirection {
  return (AUTHORITY_MESSAGE_DIRECTIONS as readonly string[]).includes(value);
}

export function isAuthorityCaseOpen(status: string | null | undefined): boolean {
  return !!status && isAuthorityCaseStatus(status) && !AUTHORITY_CASE_TERMINAL_STATUSES.includes(status);
}

export function isAuthorityObligationOpen(status: string | null | undefined): boolean {
  const normalized = normalizeAuthorityObligationStatus(status);
  return !!normalized && (AUTHORITY_OBLIGATION_OPEN_STATUSES as readonly string[]).includes(normalized);
}

export function authorityCaseStageRank(status: string | null | undefined): number {
  if (!status || !isAuthorityCaseStatus(status)) return 99;
  return AUTHORITY_CASE_STAGE_RANK[status];
}
